import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { ParamGeneralFieldRepositoryFactory } from '@/store/dac-general-params-state/param-general-field-state/param-general-field.repository';
import { ParamGeneralFieldProps } from '@/store/dac-general-params-state/param-general-field-state/param-general-field.store';
import { ParamGeneralField, ParamGeneralFieldDto } from '@/types';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import { Subscription } from 'rxjs';
import { paramGeneralFieldSchema } from '@/validations/dac-general-params.validation';
import { form } from '@/utils/form.utils';

@Injectable({
  providedIn: 'root',
})
export class ParamGeneralFieldsService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToDynamicFieldStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.subscribeToStores();
  }

  subscribeToDynamicFieldStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe();
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe();
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToDynamicFieldStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.storeSubscriptions.push(this.multiFormAccordionService.storeRemoved$.subscribe());
  }

  getAllFieldDtos(): ParamGeneralFieldDto[] {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<ParamGeneralFieldProps>) => {
      const repo = ParamGeneralFieldRepositoryFactory.create(store);
      return repo.get<ParamGeneralFieldDto>('dto');
    });
  }

  validateAllFieldDtos(): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<ParamGeneralFieldProps>, index) => {
      const repo = ParamGeneralFieldRepositoryFactory.create(store);
      const dto = repo.get<ParamGeneralFieldDto>('dto');

      const result = paramGeneralFieldSchema.safeParse(dto);
      if (!result.success) {
        isValid = false;
        const fieldErrors = result.error.flatten().fieldErrors;
        repo.set('errors', fieldErrors);
        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.set('errors', {});
      }
    });

    return isValid;
  }

  setFieldDto(response: ParamGeneralField): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response.id?.toString() || Math.random().toString(36).substring(7));
    if (!newStore) return;

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = ParamGeneralFieldRepositoryFactory.create(newStore);
    repo.setFromResponse(response);
  }

  setFieldDtos(responses: ParamGeneralField[]): void {
    responses.forEach(r => this.setFieldDto(r));
  }

  resetAllFields() {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = ParamGeneralFieldRepositoryFactory.create(store);
      repo.reset();
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
